import {
  type MetaFunction,
  redirect,
  json,
  type ActionFunction,
  type LoaderArgs,
} from '@shopify/remix-oxygen';
import {
  Form,
  useActionData,
  useLoaderData,
  useFetcher,
  useMatches,
} from '@remix-run/react';
import {useState} from 'react';
import {getInputStyleClasses} from '~/lib/utils';
import {doLogin} from './login';
import type {CustomerCreatePayload} from '@shopify/hydrogen/storefront-api-types';
import {Link} from '~/components';
import {yupResolver} from '@hookform/resolvers/yup';
import {useForm} from 'react-hook-form';
import * as yup from 'yup';
import clsx from 'clsx';
import React from 'react';
import * as R from 'ramda';
import {
  useGoogleReCaptcha,
  GoogleReCaptchaProvider,
  GoogleReCaptcha,
} from 'react-google-recaptcha-v3';

//domains
import {CustomField} from 'domains/account';
import {type SanityFormField, registerPageQuery} from '~/domains/register';
import {
  getCountryCodeForSanityQuery,
  getLangagueCodeForSanityQuery,
  isTWSite,
} from '~/domains/sanity';

//components
import AccountWrapper from 'components/account/AccountWrapper';
import AccountBreadcrumb from 'components/account/AccountBreadcrumb';
import FormFieldText from 'components/form/FormFieldText';

//translations
import translation from 'translations/register.json';
import type {routeLocale} from 'domains/global';
import {set} from 'date-fns';

export async function loader({context, params, request}: LoaderArgs) {
  const customerAccessToken = await context.session.get('customerAccessToken');

  if (customerAccessToken) {
    return redirect(params.lang ? `/${params.lang}/account` : '/account');
  }

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  const {language} = context.storefront.i18n;

  const data = (await registerPageQuery({
    languageCode: getLangagueCodeForSanityQuery(language),
    countryCode: isTWSite(request.url) ? 'zh-tw' : 'zh-hk',
  })) as {
    fields: SanityFormField[];
  };

  // TODO: Query for this?
  return json({
    shopName: 'Helmet King',
    translation: translation[selectedLocale],
    formFields: data.fields,
  });
}

type ActionData = {
  formError?: string;
  success?: boolean;
};

const badRequest = (data: ActionData) => json(data, {status: 400});

export const action: ActionFunction = async ({request, context, params}) => {
  const {session, storefront} = context;
  const formData = await request.formData();

  const email = formData.get('email');
  const password = formData.get('password');
  const confirmPassword = formData.get('confirm_password');
  const acceptsMarketing = formData.get('accepts_marketing');
  const firstName = formData.get('firstName');
  const lastName = formData.get('lastName');
  // const dateOfBirth = formData.get('dateOfBirth');
  // const learningToDrive = formData.get('learningToDrive');
  // const haveLicence = formData.get('haveLicence');

  const customFieldsData = JSON.parse(
    formData.get('custom_fields_data') as string,
  ) as {
    key: string;
    namespace: string;
    type: string;
    required: boolean;
    label: string;
  }[];

  if (
    !email ||
    !password ||
    typeof email !== 'string' ||
    typeof password !== 'string'
  ) {
    return badRequest({
      formError: 'Please provide both an email and a password.',
    });
  }

  if (password !== confirmPassword) {
    return badRequest({
      formError: 'Password and confirm password does not match.',
    });
  }

  const missingFields = customFieldsData.filter((cf) => {
    return cf.required && R.isNil(formData.get(cf.key));
  });

  if (!R.isEmpty(missingFields)) {
    return badRequest({
      formError: `${missingFields[0].label} is required`,
    });
  }

  const customFields = customFieldsData
    .map((cf) => {
      if (cf.type === 'boolean') {
        return {...cf, value: formData.get(cf.key) === 'on' ? 'true' : 'false'};
      }
      if (cf.type === 'list.single_line_text_field') {
        return {...cf, value: JSON.stringify(formData.getAll(cf.key))};
      }
      return {...cf, value: formData.get(cf.key)};
    })
    .filter(
      (cf) =>
        cf.key !== 'password' &&
        cf.key !== 'confirm_password' &&
        cf.key !== 'email' &&
        cf.key !== 'first_name' &&
        cf.key !== 'last_name' &&
        cf.key !== 'accepts_marketing',
    )
    .filter((cf) => !R.isNil(cf.value)) as CustomField[];

  try {
    const data = await storefront.mutate<{
      customerCreate: CustomerCreatePayload;
    }>(CUSTOMER_CREATE_MUTATION, {
      variables: {
        input: {
          email,
          password,
          firstName,
          lastName,
          acceptsMarketing: acceptsMarketing === 'on' ? true : false,
        },
      },
    });

    if (!data?.customerCreate?.customer?.id) {
      /**
       * Something is wrong with the user's input.
       */
      throw new Error(data?.customerCreate?.customerUserErrors.join(', '));
    }

    const customerAccessToken = await doLogin(context, {email, password});
    session.set('customerAccessToken', customerAccessToken);

    const customerId = data.customerCreate.customer.id;

    try {
      const addMetafieldsRes = await fetch(
        'https://helmetking-0001.myshopify.com/admin/api/2023-01/graphql.json',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Access-Token':
              process.env.PRIVATE_SHOPIFY_ADMIN_TOKEN ?? '',
          },
          body: JSON.stringify({
            query: `
                mutation MetafieldsSet($metafields: [MetafieldsSetInput!]!) {
                  metafieldsSet(metafields: $metafields) {
                    metafields {
                      key
                      namespace
                      value
                      createdAt
                      updatedAt
                    }
                    userErrors {
                      field
                      message
                      code
                    }
                  }
                }
            `,
            variables: {
              metafields: customFields.map((cf) => ({
                ...cf,
                ownerId: customerId,
              })),
            },
          }),
        },
      );

      // const addMetafieldsResJson = (await addMetafieldsRes.json()) as any;

      return redirect(params.lang ? `/${params.lang}/account` : '/account', {
        headers: {
          'Set-Cookie': await session.commit(),
        },
      });
    } catch (err) {
      console.log(err);
    }
  } catch (error: any) {
    if (storefront.isApiError(error)) {
      console.log(error.message);
      return badRequest({
        formError: 'Something went wrong. Please try again later.',
      });
    }

    /**
     * The user did something wrong, but the raw error from the API is not super friendly.
     * Let's make one up.
     */
    return badRequest({
      formError:
        'Sorry. We could not create an account with this email. User might already exist, try to login instead.',
    });
  }
};

export const meta: MetaFunction = () => {
  return {
    title: 'Register',
  };
};

export default function Register() {
  const actionData = useActionData<ActionData>();
  const {translation, formFields} = useLoaderData<typeof loader>();

  const customFieldsData = formFields.map((field) => {
    if (field._type === 'fields.text') {
      return {
        key: field.name,
        namespace: 'customer_fields',
        type: 'single_line_text_field',
        required: field.required,
        label: field.label,
      };
    }
    if (field._type === 'fields.textarea') {
      return {
        key: field.name,
        namespace: 'customer_fields',
        type: 'multi_line_text_field',
        required: field.required,
        label: field.label,
      };
    }
    if (field._type === 'fields.date') {
      return {
        key: field.name,
        namespace: 'customer_fields',
        type: 'date',
        required: field.required,
        label: field.label,
      };
    }
    if (field._type === 'fields.checkbox') {
      return {
        key: field.name,
        namespace: 'customer_fields',
        type: 'boolean',
        required: field.required,
        label: field.label,
      };
    }
    if (field._type === 'fields.select' && field.allow_multiple) {
      return {
        key: field.name,
        namespace: 'customer_fields',
        type: 'list.single_line_text_field',
        required: field.required,
        label: field.label,
      };
    }
    return {
      key: field.name,
      namespace: 'customer_fields',
      type: 'single_line_text_field',
      required: field.required,
      label: field.label,
    };
  });

  const fetcher = useFetcher();

  const formRef = React.useRef<HTMLFormElement>(null);

  const [token, setToken] = React.useState('');

  const [root] = useMatches();
  const recaptchaKey =
    (root?.data as {ENV?: {RECAPTCHA_KEY?: string}} | undefined)?.ENV
      ?.RECAPTCHA_KEY ?? '';

  if (typeof window === 'undefined') {
    return <div className="">loading</div>;
  }

  return (
    <GoogleReCaptchaProvider
      useRecaptchaNet
      // Same latent SSR crash as the contact form: read the key from root
      // loader data, which exists on both server and client.
      reCaptchaKey={recaptchaKey}
      scriptProps={{async: true, defer: true, appendTo: 'body'}}
    >
      <AccountBreadcrumb
        currentPageLabel={translation.register ?? 'Create Account'}
        homePageLabel={translation.home ?? 'Home'}
      />
      <header className="mb-[-50px] text-center">
        <h1 className="mb-8 text-xl font-semibold">{translation.title}</h1>
        <p className="">{translation.description}</p>
      </header>
      <AccountWrapper type="xl">
        <fetcher.Form method="post" ref={formRef}>
          {actionData?.formError && (
            <div className="mb-6 flex items-center justify-center rounded-sm border border-red p-4 text-sm text-red">
              <p className="">{actionData.formError}</p>
            </div>
          )}
          <div className="space-y-12">
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {formFields?.map((field) => {
                  if (field._type === 'fields.text') {
                    return (
                      <fieldset
                        key={field._key}
                        className={clsx(
                          'w-full space-y-1',
                          field.is_full_width && 'col-span-2',
                        )}
                      >
                        <label
                          htmlFor={field.name}
                          className="text-sm text-darkGray"
                        >
                          {field.label} {field.required && '*'}
                        </label>
                        <input
                          type="text"
                          name={field.name}
                          id={field.name}
                          className={clsx([
                            'w-full appearance-none rounded-xs border py-2 px-3 text-sm leading-field',
                            'disabled:bg-gray/50 disabled:opacity-50',
                            'focus:outline-1',
                            // error ? 'border-red' : 'border-darkGray/50',
                          ])}
                          required={field.required}
                        />
                      </fieldset>
                    );
                  }
                  if (field._type === 'fields.email') {
                    return (
                      <fieldset
                        key={field._key}
                        className={clsx(
                          'w-full space-y-1',
                          field.is_full_width && 'col-span-2',
                        )}
                      >
                        <label
                          htmlFor={field.name}
                          className="text-sm text-darkGray"
                        >
                          {field.label} {field.required && '*'}
                        </label>
                        <input
                          type="email"
                          name={field.name}
                          id={field.name}
                          className={clsx([
                            'w-full appearance-none rounded-xs border py-2 px-3 text-sm leading-field',
                            'disabled:bg-gray/50 disabled:opacity-50',
                            'focus:outline-1',
                            // error ? 'border-red' : 'border-darkGray/50',
                          ])}
                          required={field.required}
                        />
                      </fieldset>
                    );
                  }
                  if (field._type === 'fields.date') {
                    return (
                      <fieldset
                        key={field._key}
                        className={clsx(
                          'w-full space-y-1',
                          field.is_full_width && 'col-span-2',
                        )}
                      >
                        <label
                          htmlFor={field.name}
                          className="text-sm text-darkGray"
                        >
                          {field.label} {field.required && '*'}
                        </label>
                        <input
                          type="date"
                          name={field.name}
                          id={field.name}
                          className={clsx([
                            'w-full appearance-none rounded-xs border py-2 px-3 text-sm leading-field',
                            'disabled:bg-gray/50 disabled:opacity-50',
                            'focus:outline-1',
                            // error ? 'border-red' : 'border-darkGray/50',
                          ])}
                          required={field.required}
                        />
                      </fieldset>
                    );
                  }
                  if (
                    field._type === 'fields.password' ||
                    field._type === 'fields.confirmPassword'
                  ) {
                    return (
                      <fieldset
                        key={field._key}
                        className={clsx(
                          'w-full space-y-1',
                          field.is_full_width && 'col-span-2',
                        )}
                      >
                        <label
                          htmlFor={field.name}
                          className="text-sm text-darkGray"
                        >
                          {field.label} {field.required && '*'}
                        </label>
                        <input
                          type="password"
                          name={field.name}
                          id={field.name}
                          className={clsx([
                            'w-full appearance-none rounded-xs border py-2 px-3 text-sm leading-field',
                            'disabled:bg-gray/50 disabled:opacity-50',
                            'focus:outline-1',
                            // error ? 'border-red' : 'border-darkGray/50',
                          ])}
                          required={field.required}
                        />
                      </fieldset>
                    );
                  }
                  if (field._type === 'fields.checkbox') {
                    return (
                      <fieldset
                        key={field._key}
                        className={clsx(
                          'flex items-center space-x-2',
                          field.is_full_width && 'col-span-2',
                        )}
                      >
                        <input
                          type="checkbox"
                          className="h-5 w-5 border"
                          name={field.name}
                          id={field.name}
                          disabled={
                            fetcher.state === 'submitting' ||
                            actionData?.success
                          }
                          required={field.required}
                        />
                        <label
                          htmlFor=""
                          className="text-sm font-normal md:text-md"
                        >
                          {field.label}
                        </label>
                      </fieldset>
                    );
                  }
                  if (field._type === 'fields.select') {
                    return (
                      <fieldset
                        key={field._key}
                        className={clsx(
                          'flex items-center space-y-2 flex-col',
                          field.is_full_width && 'col-span-2',
                        )}
                      >
                        <label
                          htmlFor={field.name}
                          className="text-sm text-darkGray w-full"
                        >
                          {field.label} {field.required && '*'}
                        </label>
                        {field.allow_multiple ? (
                          <div className="w-full grid gap-2">
                            {field.options?.map((option) => (
                              <div
                                className="flex items-center space-x-2"
                                key={`select-multiple-${option}`}
                              >
                                <input
                                  type="checkbox"
                                  className="h-5 w-5 border"
                                  name={field.name}
                                  id={field.name}
                                  value={option}
                                />
                                <label className="">{option}</label>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <select
                            name={field.name}
                            id={field.name}
                            className="w-full"
                            required={field.required}
                          >
                            {field?.options?.map((option) => (
                              <option
                                key={`select-option-${option}`}
                                value={option}
                                className=""
                              >
                                {option}
                              </option>
                            ))}
                          </select>
                        )}
                      </fieldset>
                    );
                  }
                  if (field._type === 'fields.radio') {
                    return (
                      <fieldset
                        key={field._key}
                        className={clsx(
                          'flex items-center space-x-2 flex-wrap',
                          field.is_full_width && 'col-span-2',
                        )}
                      >
                        <label className="text-sm text-darkGray w-full shrink-0 mb-2">
                          {field.label}
                        </label>
                        {field?.options?.map((option) => {
                          return (
                            <React.Fragment key={`radio-${option}`}>
                              <input
                                id={`radio-${option}`}
                                value={option}
                                name={field.name}
                                className=""
                                type="radio"
                                required={field.required}
                              />
                              <label
                                htmlFor={`radio-${option}`}
                                className="text-sm text-darkGray"
                              >
                                {option}
                              </label>
                            </React.Fragment>
                          );
                        })}
                      </fieldset>
                    );
                  }
                  return <></>;
                })}
                <input
                  type="hidden"
                  value={JSON.stringify(customFieldsData)}
                  name="custom_fields_data"
                />
              </div>
            </div>
          </div>
          <div className="mt-8 space-y-4">
            <button
              disabled={
                !token || fetcher.state === 'submitting' || actionData?.success
              }
              type="submit"
              className={clsx(
                'flex h-[2.5rem] items-center justify-center overflow-hidden p-4 text-sm font-bold duration-200 ease-out disabled:opacity-20 disabled:bg-opacity-100 hover:opacity-80 w-full rounded bg-hk-orange text-white',
              )}
            >
              {fetcher.state === 'submitting' ? (
                <>{translation?.submitting_label ?? 'Submitting...'}</>
              ) : (
                <>{translation?.submit_button ?? 'Become a member'}</>
              )}
            </button>

            {fetcher?.data?.formError && (
              <div className="text-hk-red text-sm">
                {fetcher.data.formError}
              </div>
            )}

            <RecaptchaComponent token={token} setToken={setToken} />

            <div className="flex justify-between">
              <p className="text-sm">
                {translation?.has_account ?? 'Already have an account?'} &nbsp;
                <Link className="inline underline" to="/account/login">
                  {translation?.login ?? 'Sign in'}
                </Link>
              </p>
            </div>
          </div>
        </fetcher.Form>
      </AccountWrapper>
    </GoogleReCaptchaProvider>
  );
}

function RecaptchaComponent({
  token,
  setToken,
}: {
  token: string;
  setToken: React.Dispatch<React.SetStateAction<string>>;
}) {
  const {executeRecaptcha} = useGoogleReCaptcha();
  const [dynamicAction, setDynamicAction] = React.useState('homepage');
  const [actionToChange, setActionToChange] = React.useState('');

  const clickHandler = React.useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }

    const result = await executeRecaptcha('dynamicAction');

    setToken(result);
  }, [dynamicAction, executeRecaptcha]);

  const handleTextChange = React.useCallback((event: any) => {
    setActionToChange(event.target.value);
  }, []);

  const handleCommitAction = React.useCallback(() => {
    setDynamicAction(actionToChange);
  }, [actionToChange]);

  React.useEffect(() => {
    if (!executeRecaptcha || !dynamicAction) {
      return;
    }

    const handleReCaptchaVerify = async () => {
      const token = await executeRecaptcha(dynamicAction);
      setToken(token);
    };

    handleReCaptchaVerify();
  }, [executeRecaptcha, dynamicAction]);
  return (
    <div>
      {/* <div>
        <p>Current ReCaptcha action: {dynamicAction}</p>
        <input type="text" onChange={handleTextChange} value={actionToChange} />
        <button onClick={handleCommitAction}>Change action</button>
      </div>
      <br /> */}
      {/* <button onClick={clickHandler}>Run verify</button> */}
    </div>
  );
}

const CUSTOMER_CREATE_MUTATION = `#graphql
  mutation customerCreate($input: CustomerCreateInput!) {
    customerCreate(input: $input) {
      customer {
        id
      }
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;
